/**
 * The "Access" tab: everything that decides what this account may do, plus the one action that
 * ends it.
 *
 * Contents were the middle third of the old detail drawer — account status, the silent-adventures
 * flag, the roles editor, the diagnostics invite — with the drawer's footer Delete button moved to
 * the bottom as a marked-off danger area. On a phone that ordering matters: Delete is now the last
 * thing you reach by scrolling rather than a permanently pinned red button sitting under your
 * thumb.
 */

import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import { Delete, VolumeOff } from '@mui/icons-material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { adminApi } from '@api/endpoints/admin'
import type { AdminUserDto } from '@api/types/admin'
import { UserFlags } from '@api/types/auth'
import { hasUserFlag } from '@utils/userFlags'
import { UserRolesEditor } from './UserRolesEditor'
import { ModeratorRegionsEditor } from './ModeratorRegionsEditor'
import { UserDiagnosticsPanel } from './UserDiagnosticsPanel'
import { ADMIN_USERS_KEY, adminUserKey } from './userQueryKeys'
import { errorMessage, userLabel } from './userFormatting'
import { useIsAdmin } from './useIsAdmin'

export interface UserAccessPanelProps {
  user: AdminUserDto
}

export function UserAccessPanel({ user }: UserAccessPanelProps) {
  // A Moderator gets the read-only summary: status, flags and roles, with no control and no danger zone.
  const isAdmin = useIsAdmin()
  if (!isAdmin) return <UserAccessSummary user={user} />
  return <UserAccessEditor user={user} />
}

/** What decides this account's access, read-only (Moderators). */
function UserAccessSummary({ user }: UserAccessPanelProps) {
  const roles = user.roles || []
  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="subtitle2" gutterBottom>
        Account Status
      </Typography>
      <Chip label={user.isActive ? 'Active' : 'Inactive'} color={user.isActive ? 'success' : 'default'} />

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        User Flags
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VolumeOff fontSize="small" />
        <Typography variant="body2">
          Can create silent adventures:{' '}
          {hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures) ? 'Yes' : 'No'}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        Roles
      </Typography>
      {roles.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No roles
        </Typography>
      ) : (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {roles.map((role) => (
            <Chip key={role} label={role} size="small" variant="outlined" />
          ))}
        </Stack>
      )}
    </Paper>
  )
}

function UserAccessEditor({ user }: UserAccessPanelProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [deleteOpen, setDeleteOpen] = useState(false)
  // Deactivating locks the person out mid-session, so it asks first; re-activating does not.
  const [deactivateOpen, setDeactivateOpen] = useState(false)

  const invalidateUser = () => {
    queryClient.invalidateQueries({ queryKey: adminUserKey(user.id) })
    queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
  }

  const toggleStatus = useMutation({
    mutationFn: () => adminApi.updateUserStatus(user.id, { isActive: !user.isActive }),
    onSuccess: () => {
      setDeactivateOpen(false)
      invalidateUser()
    },
  })

  const toggleSilentFlag = useMutation({
    mutationFn: () => {
      const hasSilentFlag = hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)
      // The endpoint takes flags to add OR flags to remove, never a replacement value, so the
      // current state decides which side of the request to fill in.
      return hasSilentFlag
        ? adminApi.updateUserFlags(user.id, { removeFlags: UserFlags.CanCreateSilentAdventures })
        : adminApi.updateUserFlags(user.id, { addFlags: UserFlags.CanCreateSilentAdventures })
    },
    onSuccess: invalidateUser,
  })

  const deleteUser = useMutation({
    mutationFn: () => adminApi.deleteUser(user.id),
    onSuccess: () => {
      setDeleteOpen(false)
      queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY })
      // Nothing left to show on this route once the account is gone.
      navigate('/users')
    },
  })

  const mutationError =
    (toggleStatus.error && errorMessage(toggleStatus.error, 'Failed to update user status')) ||
    (toggleSilentFlag.error && errorMessage(toggleSilentFlag.error, 'Failed to update user flags')) ||
    (deleteUser.error && errorMessage(deleteUser.error, 'Failed to delete user')) ||
    null

  return (
    <Box>
      {mutationError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {mutationError}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Account Status
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Chip
            label={user.isActive ? 'Active' : 'Inactive'}
            color={user.isActive ? 'success' : 'default'}
          />
          <Button
            variant="outlined"
            size="small"
            color={user.isActive ? 'warning' : 'success'}
            onClick={() => (user.isActive ? setDeactivateOpen(true) : toggleStatus.mutate())}
            disabled={toggleStatus.isPending}
          >
            {toggleStatus.isPending ? 'Updating...' : user.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          User Flags
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)}
              onChange={() => toggleSilentFlag.mutate()}
              disabled={toggleSilentFlag.isPending}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VolumeOff fontSize="small" />
              Can Create Silent Adventures
            </Box>
          }
        />
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        {/* Remounted whenever the saved roles change, which is how the staged edit is re-seeded. */}
        <UserRolesEditor key={[...(user.roles || [])].sort().join(',')} userId={user.id} roles={user.roles || []} />
      </Paper>

      {/* Shown to anyone holding regions or the role; otherwise it would be clutter on every account. */}
      {(user.roles || []).includes('RegionalModerator') && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
          <ModeratorRegionsEditor userId={user.id} hasRole />
        </Paper>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <UserDiagnosticsPanel userId={user.id} />
      </Paper>

      {/* Danger area — visually separated so the delete button is never mistaken for one of the
          settings above it. */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderColor: 'error.main' }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle2" color="error">
            Danger zone
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Deleting removes the account outright. Deactivating instead keeps their history and
            locks them out, and it can be undone.
          </Typography>
          <Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteOpen(true)}
              disabled={deleteUser.isPending}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Delete user
            </Button>
          </Box>
        </Stack>
      </Paper>

      <ConfirmDialog
        open={deactivateOpen}
        title="Deactivate user?"
        message={`${userLabel(user)} will be locked out until the account is activated again. Their history is kept.`}
        confirmLabel="Deactivate"
        destructive
        loading={toggleStatus.isPending}
        onConfirm={() => toggleStatus.mutate()}
        onClose={() => setDeactivateOpen(false)}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete User"
        message={`Are you sure you want to delete ${userLabel(user)}? This action cannot be undone.`}
        confirmLabel={deleteUser.isPending ? 'Deleting...' : 'Delete'}
        destructive
        loading={deleteUser.isPending}
        onConfirm={() => deleteUser.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </Box>
  )
}
